import { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl } from '../../config/apiBase';
import { adminFetchInit } from '../../config/adminApi';
import {
  extractEmailProbeRowsFromSearch,
  probeRowKey,
  verdictLabel,
  verdictToneClass,
} from './adminEmailReviewUtils';

const VERDICTS = [
  { id: 'correct', label: 'Correct', short: 'OK' },
  { id: 'false_positive', label: 'False positive', short: 'FP' },
  { id: 'missed', label: 'Should be registered', short: 'Missed' },
];

function resolveEmailTarget(search, rows) {
  const fromQuery = String(search?.query?.email || '').trim().toLowerCase();
  if (fromQuery.includes('@')) return fromQuery;
  const fromRow = rows.find((r) => String(r.target || '').includes('@'));
  return fromRow ? String(fromRow.target).trim().toLowerCase() : '';
}

export default function AdminEmailProbeReview({ search }) {
  const base = getApiBaseUrl();
  const rows = useMemo(() => extractEmailProbeRowsFromSearch(search), [search]);
  const email = useMemo(() => resolveEmailTarget(search, rows), [search, rows]);
  const [feedbackByKey, setFeedbackByKey] = useState({});
  const [notesByKey, setNotesByKey] = useState({});
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState('');

  const loadFeedback = useCallback(async () => {
    if (!search?.id) return;
    setError('');
    try {
      const params = new URLSearchParams({ search_id: String(search.id), limit: '200' });
      const res = await fetch(`${base}/admin/probe-feedback?${params}`, adminFetchInit());
      if (!res.ok) throw new Error(`feedback HTTP ${res.status}`);
      const data = await res.json();
      const map = {};
      for (const item of data.items || []) {
        const key = [
          item.row_kind,
          item.service_key,
          item.source || '',
          item.probe_state || '',
          item.probe_detail || '',
        ].join('|');
        map[key] = item;
      }
      setFeedbackByKey(map);
    } catch (exc) {
      setError(String(exc?.message || exc));
    }
  }, [base, search?.id]);

  useEffect(() => {
    setFeedbackByKey({});
    setNotesByKey({});
    loadFeedback();
  }, [loadFeedback]);

  const submitVerdict = useCallback(
    async (row, verdict) => {
      const key = probeRowKey(row);
      setSavingKey(key);
      setError('');
      try {
        const res = await fetch(`${base}/admin/probe-feedback`, adminFetchInit({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            search_id: search?.id,
            email,
            service_key: row.service_key,
            service_name: row.service_name,
            row_kind: row.row_kind,
            source: row.source,
            status_label: row.status_label,
            probe_state: row.probe_state,
            probe_detail: row.probe_detail,
            probe_method: row.probe_method,
            verdict,
            notes: notesByKey[key] || '',
          }),
        }));
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `feedback HTTP ${res.status}`);
        }
        const data = await res.json();
        setFeedbackByKey((prev) => ({ ...prev, [key]: data.item }));
      } catch (exc) {
        setError(String(exc?.message || exc));
      } finally {
        setSavingKey('');
      }
    },
    [base, email, notesByKey, search?.id],
  );

  if (!rows.length) {
    return (
      <section>
        <h3 className="text-[10px] font-black uppercase tracking-wider text-slate-500">Email probe review</h3>
        <p className="mt-2 text-xs leading-relaxed text-slate-400">
          No registered platforms or native probe badges in this scan replay yet. Run a fresh email scan and reopen
          this investigation after the stream completes.
        </p>
      </section>
    );
  }

  return (
    <section>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[10px] font-black uppercase tracking-wider text-slate-500">Email probe review</h3>
          <p className="mt-1 text-xs text-slate-300">
            Mark hits against your ground truth for <span className="font-mono text-cyan-300">{email || '—'}</span>.
          </p>
        </div>
        <span className="rounded-full border border-slate-600 bg-slate-800 px-2 py-0.5 text-[10px] font-bold text-slate-300">
          {rows.length} row{rows.length === 1 ? '' : 's'}
        </span>
      </div>

      {error ? (
        <p className="mt-3 rounded-lg border border-red-500/30 bg-red-950/30 px-3 py-2 text-xs text-red-200">{error}</p>
      ) : null}

      <ul className="mt-4 space-y-3">
        {rows.map((row) => {
          const key = probeRowKey(row);
          const saved = feedbackByKey[key];
          const busy = savingKey === key;
          return (
            <li
              key={key}
              className="rounded-xl border border-slate-700 bg-slate-950/70 p-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-100">{row.service_name}</p>
                  <p className="mt-0.5 font-mono text-[10px] text-slate-500">
                    {row.row_kind === 'registered_service' ? 'Registered' : 'Probe badge'}
                    {row.source ? ` · ${row.source}` : ''}
                    {row.probe_state ? ` · ${row.probe_state}` : ''}
                  </p>
                  {row.probe_detail ? (
                    <p className="mt-1 text-[10px] text-slate-500">{row.probe_detail}</p>
                  ) : null}
                </div>
                {saved?.verdict ? (
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${verdictToneClass(saved.verdict)}`}>
                    {verdictLabel(saved.verdict)}
                  </span>
                ) : null}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {VERDICTS.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    disabled={busy}
                    onClick={() => submitVerdict(row, v.id)}
                    className={`rounded-lg border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide transition ${
                      saved?.verdict === v.id
                        ? verdictToneClass(v.id)
                        : 'border-slate-600 bg-slate-900 text-slate-300 hover:border-slate-500 hover:bg-slate-800'
                    } ${busy ? 'opacity-50' : ''}`}
                  >
                    {v.short}
                  </button>
                ))}
              </div>

              <label className="mt-2 block">
                <span className="sr-only">Notes for {row.service_name}</span>
                <input
                  type="text"
                  value={notesByKey[key] ?? saved?.notes ?? ''}
                  onChange={(e) => setNotesByKey((prev) => ({ ...prev, [key]: e.target.value }))}
                  placeholder="Optional note (e.g. known account on Spotify)"
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-[11px] text-slate-200 placeholder:text-slate-500 focus:border-cyan-500/40 focus:outline-none"
                />
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
